import {
  Button,
  CheckboxBlockField,
  InformationBox,
  TitleWithIcon,
} from 'components';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';

import {
  Participation,
  ParticipationMutationOptions,
  ParticipationStatus,
  ParticipationUpdateData,
} from '@app/api';
import { Theme } from '@app/theme';
import { MeetingRatingSelector } from './MeetingRatingSelector';
import { MeetingReportReasonSelect } from './MeetingReportReasonSelect';
import { MutationOptions } from 'react-query';

const useStyles = createUseStyles((theme: Theme) => ({
  iconGreen: {
    color: theme.colors.green,
  },
  iconRed: {
    color: theme.colors.red,
  },
  iconBlue: {
    color: theme.colors.blue,
  },
  evaluationButtonsContainer: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.marginBase * 2,
  },
  fullLine: {
    gridColumn: '1 / span 2',
    gridRow: 1,
  },
  textSelect: {
    width: '100%',
  },
  rescheduleButton: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
  },
  automaticValidationText: {
    marginTop: theme.marginBase * 2,
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
}));

interface Props {
  participation: Participation;
  reschedule?: (id: string) => any;
  className?: string;
  withoutTitle?: boolean;
  reportMutationOptions?: ParticipationMutationOptions;
  payMutationOptions?: ParticipationMutationOptions;
}

enum MeetingConductStatus {
  MEETING_TOOK_PLACE = 'MEETING_TOOK_PLACE',
  MEETING_DID_NOT_TAKE_PLACE = 'MEETING_DID_NOT_TAKE_PLACE',
  RESCHEDULE = 'RESCHEDULE',
}

export const MeetingRating = ({
  participation,
  className,
  reschedule,
  withoutTitle = false,
  payMutationOptions,
  reportMutationOptions,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const [meetingConductStatus, setMeetingConductStatus] = useState<
    undefined | MeetingConductStatus
  >();

  const onMeetingTookPlaceClick = useCallback(() => {
    setMeetingConductStatus(MeetingConductStatus.MEETING_TOOK_PLACE);
  }, []);

  const onMeetingDidNotTakePlaceClick = useCallback(() => {
    setMeetingConductStatus(MeetingConductStatus.MEETING_DID_NOT_TAKE_PLACE);
  }, []);

  const onRescheduleOptionClick = useCallback(() => {
    setMeetingConductStatus(MeetingConductStatus.RESCHEDULE);
  }, []);

  const onRescheduleClick = useCallback(() => {
    reschedule && reschedule(participation.id);
  }, [participation.id, reschedule]);

  const shouldDisplayMeetingRatingOrReport =
    participation.status === ParticipationStatus.PARTICIPATED;

  const shouldDisplayInformationBox =
    [
      ParticipationStatus.PAYMENT_REQUESTED,
      ParticipationStatus.PROCESSING_PAYMENT,
      ParticipationStatus.PAYED,
      ParticipationStatus.NO_SHOW,
    ].includes(participation.status) && !!participation.reportReason;

  if (!shouldDisplayMeetingRatingOrReport && !shouldDisplayInformationBox) {
    return null;
  }

  return (
    <div className={className}>
      {!withoutTitle && (
        <TitleWithIcon
          icon="ri-star-line"
          iconColor="blue"
          title={t('sessions.details.monitoring.meetingRating.title')}
        />
      )}
      {shouldDisplayMeetingRatingOrReport && (
        <>
          <div className={classes.evaluationButtonsContainer}>
            <div className={classes.fullLine}>
              <CheckboxBlockField
                label={t(
                  'sessions.details.monitoring.meetingRating.meetingTookPlace',
                )}
                type="radio"
                variant="green"
                onChange={onMeetingTookPlaceClick}
                checked={
                  meetingConductStatus ===
                  MeetingConductStatus.MEETING_TOOK_PLACE
                }
                icon="ri-thumb-up-line"
                iconClassName={classes.iconGreen}
              />
            </div>
            <div>
              <CheckboxBlockField
                label={t(
                  'sessions.details.monitoring.meetingRating.reschedule',
                )}
                type="radio"
                variant="greenSelectedNoIconColor"
                onChange={onRescheduleOptionClick}
                checked={
                  meetingConductStatus === MeetingConductStatus.RESCHEDULE
                }
                icon="ri-calendar-event-line"
                iconClassName={classes.iconBlue}
              />
            </div>
            <div>
              <CheckboxBlockField
                label={t(
                  'sessions.details.monitoring.meetingRating.meetingDidNotTakePlace',
                )}
                type="radio"
                variant="greenSelectedNoIconColor"
                onChange={onMeetingDidNotTakePlaceClick}
                checked={
                  meetingConductStatus ===
                  MeetingConductStatus.MEETING_DID_NOT_TAKE_PLACE
                }
                icon="ri-thumb-down-line"
                iconClassName={classes.iconRed}
              />
            </div>
          </div>
          {meetingConductStatus ===
            MeetingConductStatus.MEETING_DID_NOT_TAKE_PLACE && (
            <MeetingReportReasonSelect
              participation={participation}
              mutationOptions={reportMutationOptions}
            />
          )}
          {meetingConductStatus === MeetingConductStatus.MEETING_TOOK_PLACE && (
            <MeetingRatingSelector
              participation={participation}
              mutationOptions={payMutationOptions}
            />
          )}
          {meetingConductStatus === MeetingConductStatus.RESCHEDULE && (
            <Button
              onClick={onRescheduleClick}
              text={t(
                'sessions.details.monitoring.meetingRating.selectNewAppointment',
              )}
              className={classes.rescheduleButton}
              variant="transparent"
            />
          )}
        </>
      )}
      {shouldDisplayInformationBox && !participation.reportReason && (
        <InformationBox
          variant="thumb-up"
          text={t(
            'sessions.details.monitoring.meetingRating.afterMeetingDisplay.MEETING_TOOK_PLACE',
          )}
        />
      )}
      {shouldDisplayInformationBox && participation.reportReason && (
        <InformationBox
          variant="thumb-down"
          text={
            <Trans
              i18nKey="sessions.details.monitoring.meetingRating.afterMeetingDisplay.MEETING_DID_NOT_TAKE_PLACE"
              values={{
                reportReason: t(
                  `sessions.details.monitoring.meetingRating.participationReport.participationReportReason.${participation.reportReason}`,
                ),
              }}
              components={{
                strong: <strong />,
              }}
            />
          }
        />
      )}
      {!shouldDisplayInformationBox && participation.automaticlyValidateAt && (
        <p className={classes.automaticValidationText}>
          {t(
            'sessions.details.monitoring.meetingRating.automaticlyValidateAt',
            {
              date: participation.automaticlyValidateAt,
            },
          )}
        </p>
      )}
    </div>
  );
};
